/* eslint-disable jsx-a11y/iframe-has-title */
'use client';
import Link from 'next/link';
import { type DemoPageData } from './demoPageData.copy';
import { ArrowLeftIcon, ArrowPathRoundedSquareIcon, ArrowsPointingOutIcon } from '@heroicons/react/20/solid';
import {
  ChartBarSquareIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';
import { ReactNode, useRef } from 'react';
import Button from '@/components/Button';
import { type KeyBenefitsFeaturesCI, type validPageNames } from './demoPageData.copy';
import { demoPageURI } from '../attribute-page/NavItems';

interface DemoDetailProps {
  demoData: DemoPageData;
}

interface FeaturesBenefitsULProps {
  section: KeyBenefitsFeaturesCI[];
  title: string;
}

const FeaturesBenefitsUL = ({ section, title }: FeaturesBenefitsULProps) => {
  return (
    <>
      <h3 className={'text-h6 mt-5'}>{title}</h3>
      <ul className="[&_h4]:text-h6 flex list-disc flex-col gap-6">
        {section.map((item, idx) => (
          <li key={idx + '_item'} className="ml-6">
            <p className="text-body text-gray-5">
              {item.title && <strong className="text-body-bold text-light-blue">{item.title}: </strong>}
              {item.content}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
};

const RelatedFeatureInfo = ({ demoData }: DemoDetailProps) => {
  return (
    <div className="flex flex-col gap-6">
      <h3 className={'text-h6'}>Related Feature Info and Customer Stories</h3>
      <ul className="flex flex-col gap-8">
        {demoData.relatedLinks?.map((item, idx) => (
          <li key={idx + '_item'} className="text-gradle-blue">
            <Link className="flex gap-2 hover:underline hover:underline-offset-4" href={item.url}>
              <span>
                {item.title.startsWith('Case Study') && (
                  <DocumentTextIcon className="mt-[1px] size-5 md:mt-[2px] lg:mt-[3px]" />
                )}
                {item.title.startsWith('Product Page') && (
                  <ChartBarSquareIcon className="mt-[1px] size-5 md:mt-[2px] lg:mt-[3px]" />
                )}
                {item.title.startsWith('Blog Post') && (
                  <ClipboardDocumentIcon className="mt-[1px] size-5 md:mt-[2px] lg:mt-[3px]" />
                )}
                {item.title.startsWith('Documentation') && (
                  <DocumentIcon className="mt-[1px] size-5 md:mt-[2px] lg:mt-[3px]" />
                )}
              </span>
              {item.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const DemoDetail = ({ demoData }: DemoDetailProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  type PageIconDictOptions = {
    [key in validPageNames]: ReactNode;
  };

  const pageIconDict: PageIconDictOptions = {
    troubleshoot: <ArrowPathRoundedSquareIcon />,
    observe: <ChartBarSquareIcon />,
    accelerate: <CubeTransparentIcon />,
  } as const;

  const handleFullScreen = () => {
    iframeRef.current?.requestFullscreen();
  };

  return (
    <div className="min-h-screen bg-dark-background px-4 py-6 pb-24 text-white md:px-6 md:pb-14 lg:px-0 lg:pb-32">
      <div className="max-w-section grid grid-cols-12 gap-10 gap-x-0 md:gap-x-6">
        <div className="col-span-12 col-start-1 flex w-full flex-col items-center gap-6">
          <Link
            href={`${demoPageURI}/${demoData.backPage}/`}
            className="flex items-center gap-2 self-start px-2 py-2.5 md:px-5"
          >
            <ArrowLeftIcon className="size-5" /> Back to Demos
          </Link>
          <div className="flex w-fit rounded-2xl p-4 shadow-card-dark-2 md:rounded-3xl md:p-5">
            <div className="size-8 text-gradle-blue md:size-14">{pageIconDict[demoData.backPage]}</div>
          </div>
        </div>
        <div className="col-span-12 col-start-1 flex w-full flex-col gap-12 lg:col-span-12 lg:gap-16">
          <h1 className="text-h4 max-w-[48rem] self-center text-center">{demoData.title}</h1>
          <div className="flex h-[21.5rem] w-full flex-col items-end rounded-2xl bg-gradle-blue md:h-[35rem] lg:h-[42rem]">
            <Button
              onClick={handleFullScreen}
              variant="ghost-light"
              className="relative !rounded-tr-2xl rounded-br-none"
            >
              <ArrowsPointingOutIcon className="size-5" />
            </Button>

            <div className="relative h-full w-full scale-[1]">
              <iframe
                ref={iframeRef}
                className="fixed left-0 top-0 h-full w-full rounded-b-2xl border-none"
                src={demoData.storyLaneUrl}
                allow="fullscreen"
              />
            </div>
          </div>
        </div>
        <div className="text-body col-span-12 col-start-1 flex w-full flex-col gap-6 text-gray-5 lg:col-span-8 lg:col-start-3 lg:gap-8">
          <div className="mt-5 flex gap-2 self-start">
            <h3 className={'text-h6'}>Overview</h3>
          </div>
          <div className="[&_h4]:text-h6 flex flex-col gap-6">{demoData.overview}</div>
          {demoData.keyBenefits.length > 0 && (
            <FeaturesBenefitsUL section={demoData.keyBenefits} title="Key Benefits" />
          )}
          {demoData.keyFeatures.length > 0 && (
            <FeaturesBenefitsUL section={demoData.keyFeatures} title="Key Features" />
          )}
          {demoData.keyBenefitsVsCI && demoData.keyBenefitsVsCI.length > 0 && (
            <FeaturesBenefitsUL
              section={demoData.keyBenefitsVsCI}
              title="Benefits vs. CI fan out and single-machine parallelism"
            />
          )}
          <hr className="my-12 w-full text-gray-2 md:my-14 lg:my-16" />
          <div className="flex flex-col gap-16">
            {demoData.relatedLinks.length > 0 && <RelatedFeatureInfo demoData={demoData} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoDetail;
